import { Component, OnInit, inject } from "@angular/core";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { TabService } from "@services/tabs/TabService";
import { Pages } from "@constants/pages/Pages";
import { ModuleType } from "@enums/ModuleType";
import { WorkspacesService } from "@app/workspaces/services/workspaces-data.service";

import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { nameof } from "@methods/CommonMethods";
import { AssetOverviewModel } from "@app/workspaces/models/assetoverviewmodel";
import { firstValueFrom } from "rxjs";
import { injectTranslation } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/assetmanagement/i18n/scope";

@Component({
    selector: "app-assetmanagement-overview",
    templateUrl: "./assetmanagement-overview.component.html",
    styleUrls: ["./assetmanagement-overview.component.scss"],
    // eslint-disable-next-line
    standalone: false,
})
export class AssetManagementOverviewComponent implements OnInit {
    moduleType = ModuleType.AssetManagement;
    tabID: string;
    tab: TabModel;
    data: Promise<AssetOverviewModel[]>;
    headerLookup: { [propName: string]: string } = {};
    numberProps: string[] = [nameof<AssetOverviewModel>("reference")];
    dateProps: string[] = [
        nameof<AssetOverviewModel>("dateInUse"),
        nameof<AssetOverviewModel>("dateOutOfUse"),
    ];

    permissions: WorkspaceModulePermissionModel;

    private tabService = inject(TabService);
    private workspaceService = inject(WorkspacesService);
    private pages = inject(Pages);
    private _translation = injectTranslation(scope);

    async ngOnInit() {
        // temp
        this.permissions = new WorkspaceModulePermissionModel();
        this.permissions.canAdd = true;
        this.permissions.canOpen = true;

        this.data = this.loadData();
        await this.loadHeaderTranslations();
    }

    // Merged so the outcome is independent of whether this or mapHeaders() (which fills the
    // remaining, untranslated columns) finishes first.
    private async loadHeaderTranslations() {
        await this._translation.ready();
        this.headerLookup = { ...this.headerLookup, ...this.getHeaderLookup() };
    }

    public async open(asset: AssetOverviewModel) {
        await this._translation.ready();
        this.tab.showLoader = true;
        this.tabService.generateTab(
            this.pages.AssetManagementView,
            asset.guid,
            this._translation.t("overview.assetTabTitle", {
                reference: asset.reference,
                name: asset.name,
            }),
            null,
        );
        this.tab.showLoader = false;
    }

    public async add() {
        await this._translation.ready();
        this.tabService.generateTab(
            this.pages.AssetManagementRegister,
            null,
            this._translation.t("overview.addNewAssetTab"),
        );
    }

    private async loadData() {
        const data = await firstValueFrom(this.workspaceService.getAssetWorkspaceVbeta());
        return this.mapHeaders((data ?? []).map(AssetOverviewModel.fromApi));
    }

    private mapHeaders = (rows: AssetOverviewModel[]) => {
        if (rows && rows.length) {
            Object.getOwnPropertyNames(rows[0]).forEach((propName) => {
                if (!this.headerLookup[propName]) {
                    this.headerLookup[propName] = propName;
                }
            });
        }
        return rows;
    };

    private getHeaderLookup(): { [propName: string]: string } {
        const headerLookup: { [propName: string]: string } = {};
        headerLookup[nameof<AssetOverviewModel>("reference")] = this._translation.t("overview.referenceHeader",
        );
        headerLookup[nameof<AssetOverviewModel>("name")] = this._translation.t("overview.nameHeader",
        );
        headerLookup[nameof<AssetOverviewModel>("organizationName")] =
            this._translation.t("overview.organizationNameHeader");

        headerLookup[nameof<AssetOverviewModel>("userName")] = this._translation.t("overview.userNameHeader",
        );
        headerLookup[nameof<AssetOverviewModel>("description")] = this._translation.t("overview.descriptionHeader",
        );
        headerLookup[nameof<AssetOverviewModel>("dateInUse")] = this._translation.t("overview.dateInUseHeader",
        );
        headerLookup[nameof<AssetOverviewModel>("dateOutOfUse")] = this._translation.t("overview.dateOutOfUseHeader",
        );
        headerLookup[nameof<AssetOverviewModel>("thirdPartyName")] =
            this._translation.t("overview.thirdPartyHeader");

        headerLookup[nameof<AssetOverviewModel>("contractName")] = this._translation.t("overview.contractHeader",
        );
        return headerLookup;
    }
}
